import { TextField, Textarea } from '@/shared/components/TextField';
import CreateLayout from '@/shared/layout/createLayout';
import Loader from '@/app/routes/Loader';
import ErrorPage403 from '@/app/routes/errorPage403';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { QMS_CATEGORY_LABELS } from '../../constants/labels';
import { useCreateQmsCategory } from '../../hooks/useCreateQmsCategory';
import TagInput from '../../components/TagInput/TagInput';

const CreateQmsCategory: React.FC = () => {
  const {
    formData,
    errors,
    tagError,
    isSubmitting,
    isCancelModalOpen,
    isSnackbarOpen,
    snackbarError,
    handleNameChange,
    handleDescriptionChange,
    handleTagsChange,
    handleTagPendingChange,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    tagSuggestions,
    isTagSuggestionsLoading,
    onTagSearchChange,
  } = useCreateQmsCategory();

  const { privileges, isLoading: isPrivilegesLoading } = useUserPrivileges();

  if (isPrivilegesLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (!privileges.qmsDocCategoryCreate) {
    return <ErrorPage403 />;
  }

  const Header = () => (
    <p className="text-titleLarge text-neutral-10">
      {QMS_CATEGORY_LABELS.CREATE_TITLE}
    </p>
  );

  return (
    <CreateLayout
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isSnackbarOpen}
      handleCloseSnackbar={handleCloseSnackbar}
      snackbarError={snackbarError}
      Header={Header}
      submitButtonLabel={QMS_CATEGORY_LABELS.BUTTON_CREATE}
      submitButtonLoadingLabel={QMS_CATEGORY_LABELS.BUTTON_CREATE_LOADING}
      cancelButtonLabel={QMS_CATEGORY_LABELS.BUTTON_CANCEL}
      submitButtonDisabled={isSubmitting}
    >
      <div className="flex flex-col gap-2">
        <p className="text-[18px] text-on-surface-variant">
          {QMS_CATEGORY_LABELS.SECTION_DETAILS}
        </p>
        <div className="flex flex-col gap-8">
          <div className="flex gap-4 w-[50%]">
            <TextField
              type="text"
              inputMode="text"
              label={QMS_CATEGORY_LABELS.FIELD_NAME}
              maxLength={50}
              autoFocus
              fullWidth
              autoComplete="off"
              value={formData.name}
              onChange={handleNameChange}
              error={Boolean(errors.name)}
              errorMessage={errors.name ?? ''}
              disabled={isSubmitting}
            />
          </div>
          <TagInput
            className="w-[calc(50%+5.25rem)]"
            existingTags={formData.existingTags}
            newTags={formData.newTags}
            onChange={handleTagsChange}
            disabled={isSubmitting}
            suggestions={tagSuggestions}
            isSuggestionsLoading={isTagSuggestionsLoading}
            onSearchChange={onTagSearchChange}
            error={tagError}
            onPendingChange={handleTagPendingChange}
          />

          <Textarea
            label={QMS_CATEGORY_LABELS.FIELD_DESCRIPTION}
            maxLength={500}
            fullWidth
            rows={4}
            value={formData.description}
            onChange={handleDescriptionChange}
            error={Boolean(errors.description)}
            errorMessage={errors.description ?? ''}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </CreateLayout>
  );
};

export default CreateQmsCategory;
